import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "reactflow/dist/style.css";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, { Background, BackgroundVariant, Controls, ReactFlowProvider, useEdgesState, useNodesState, useReactFlow, } from "reactflow";
import { api } from "../../lib/api";
import { detectSpreadsheetPorts } from "../../lib/spreadsheet";
import { ConfigPanel } from "./ConfigPanel";
import { EDGE_TYPES } from "./edge";
import { NODE_TYPES } from "./nodes";
import { Palette } from "./Palette";
import { TemplateDialog } from "./TemplateDialog";
import { SpreadsheetModal } from "../spreadsheet/SpreadsheetModal";
import { TimelineEditor } from "../timeline/TimelineEditor";
import { DerivativesEditor } from "../derivatives/DerivativesEditor";
import { BuildModelModal } from "./BuildModelModal";
import { BuildWithAIModal } from "./BuildWithAIModal";
export function WorkflowEditor(props) {
    return (_jsx(ReactFlowProvider, { children: _jsx(WorkflowEditorInner, { ...props }) }));
}
function WorkflowEditorInner({ version, onSave, saving, onDirtyChange }) {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [openSpreadsheetId, setOpenSpreadsheetId] = useState(null);
    const [openTimelineId, setOpenTimelineId] = useState(null);
    const [openDerivativesId, setOpenDerivativesId] = useState(null);
    const [buildModelTarget, setBuildModelTarget] = useState(null);
    const [spreadsheetPreviews, setSpreadsheetPreviews] = useState({});
    const [armedTool, setArmedTool] = useState(null);
    const [prunedEdges, setPrunedEdges] = useState([]);
    const [templateDialog, setTemplateDialog] = useState(null);
    const [buildWithAIOpen, setBuildWithAIOpen] = useState(false);
    // Snapshot of the last persisted definition (JSON) plus when it was saved.
    // We compare buildDefn() against this to detect unsaved edits.
    const [lastSavedJson, setLastSavedJson] = useState("");
    const [lastSavedAt, setLastSavedAt] = useState(version.updated_at ? new Date(version.updated_at) : null);
    // Tick once a minute so the relative-time label refreshes.
    const [, forceTimeTick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => forceTimeTick((n) => n + 1), 30_000);
        return () => clearInterval(t);
    }, []);
    const idCounter = useRef(0);
    const rf = useReactFlow();
    // Esc disarms a pending tool.
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape")
                setArmedTool(null);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    // Initialize from the loaded version (and re-init only when the version changes).
    useEffect(() => {
        const d = normalizeDefn(version.workflow_definition);
        const initialized = d.nodes.map((n) => {
            const portData = derivePortData(n.type, n.config);
            return {
                id: n.id,
                type: n.type,
                position: n.position ?? { x: 120, y: 120 },
                data: {
                    label: defaultLabel(n.type, n.config),
                    type: n.type,
                    config: n.config,
                    ...portData,
                },
            };
        });
        setNodes(initialized);
        // Drop edges whose endpoints don't correspond to any current port on
        // their respective nodes. This automatically cleans up orphaned edges
        // left behind from earlier port-id migrations.
        const inSet = new Map();
        const outSet = new Map();
        for (const n of initialized) {
            inSet.set(n.id, new Set(n.data.inputs));
            outSet.set(n.id, new Set(n.data.outputs));
        }
        const dropped = [];
        const cleanEdges = d.edges.filter((e) => {
            const ins = inSet.get(e.to_node);
            const outs = outSet.get(e.from_node);
            const ok = !!ins && ins.has(e.to_port) && !!outs && outs.has(e.from_port);
            if (!ok) {
                dropped.push({
                    from: e.from_node,
                    fromPort: e.from_port,
                    to: e.to_node,
                    toPort: e.to_port,
                });
            }
            return ok;
        });
        setEdges(cleanEdges.map((e) => ({
            id: e.id,
            source: e.from_node,
            sourceHandle: e.from_port,
            target: e.to_node,
            targetHandle: e.to_port,
            animated: false,
            style: { stroke: "#38bdf8", strokeWidth: 2 },
        })));
        setPrunedEdges(dropped);
        setSelectedId(null);
        // Seed lastSavedJson with the SAME shape buildDefn() produces, so the
        // dirty comparison after init starts as a no-op (no spurious "unsaved"
        // immediately after loading the page).
        const seedDefn = {
            nodes: initialized.map((n) => ({
                id: n.id,
                type: n.type ?? "",
                config: n.data.config ?? {},
                position: { x: n.position.x, y: n.position.y },
            })),
            edges: cleanEdges.map((e) => ({
                id: e.id,
                from_node: e.from_node,
                from_port: e.from_port,
                to_node: e.to_node,
                to_port: e.to_port,
            })),
        };
        setLastSavedJson(JSON.stringify(seedDefn));
        if (version.updated_at)
            setLastSavedAt(new Date(version.updated_at));
    }, [version.id, version.updated_at, setNodes, setEdges]);
    // Refresh spreadsheet previews when any spreadsheet's cells change.
    const cellsSignature = useMemo(() => nodes
        .filter((n) => n.type === "spreadsheet")
        .map((n) => `${n.id}:${JSON.stringify(n.data.config?.cells ?? {})}`)
        .join("|"), [nodes]);
    useEffect(() => {
        let cancelled = false;
        async function refresh() {
            const next = {};
            for (const n of nodes) {
                if (n.type !== "spreadsheet")
                    continue;
                const cells = n.data.config?.cells ?? {};
                if (Object.keys(cells).length === 0) {
                    next[n.id] = { cells: {}, outputs: {}, input_ports: [], output_ports: [], errors: {} };
                    continue;
                }
                try {
                    const r = await api.post("/workflow/spreadsheet/preview", {
                        cells,
                        inputs: {},
                    });
                    if (cancelled)
                        return;
                    next[n.id] = r;
                }
                catch {
                    next[n.id] = null;
                }
            }
            if (!cancelled)
                setSpreadsheetPreviews(next);
        }
        refresh();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellsSignature]);
    // Re-derive port lists for spreadsheet nodes whenever cells change.
    // Detection is purely client-side (regex), so handles are rendered
    // instantly — no waiting for the preview round-trip.
    useEffect(() => {
        setNodes((ns) => ns.map((n) => {
            if (n.type !== "spreadsheet")
                return n;
            const cells = n.data.config?.cells ?? {};
            const outputsCfg = n.data.config?.outputs ?? [];
            const detected = detectSpreadsheetPorts(cells, outputsCfg);
            // The spreadsheet always exposes the static "bus" input ahead of
            // any INPUT()-detected ports.
            const busIns = BUS_INPUTS["spreadsheet"] ?? [];
            const inputs = [...busIns, ...detected.inputs];
            const inputKinds = {
                ...Object.fromEntries(detected.inputs.map((k) => [k, "value"])),
                ...Object.fromEntries(busIns.map((k) => [k, "bus"])),
            };
            const outputKinds = Object.fromEntries(detected.outputs.map((k) => [k, "value"]));
            const p = spreadsheetPreviews[n.id];
            const errorCount = p ? Object.keys(p.errors).length : 0;
            if (arraysEqual(n.data.inputs, inputs) &&
                arraysEqual(n.data.outputs, detected.outputs) &&
                (n.data.errorCount ?? 0) === errorCount &&
                n.data.inputKinds?.bus === "bus") {
                return n;
            }
            return {
                ...n,
                data: {
                    ...n.data,
                    inputs,
                    outputs: detected.outputs,
                    inputKinds,
                    outputKinds,
                    errorCount,
                },
            };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellsSignature, spreadsheetPreviews]);
    // Pull this approach's scenarios so scenario_chooser nodes can derive
    // their input ports from them. Sync runs whenever scenarios change OR
    // a new scenario_chooser node is added — never persisted via the user
    // editing config.inputs directly (the panel is read-only for that).
    const scenarios = useQuery({
        queryKey: ["approach-scenarios", version.approach_id],
        queryFn: () => api.get(`/approaches/${version.approach_id}/scenarios`),
        enabled: !!version.approach_id,
    });
    const scenarioPortsSignature = useMemo(() => {
        if (!scenarios.data)
            return "";
        return scenarios.data
            .slice()
            .sort((a, b) => {
            if (a.is_base !== b.is_base)
                return a.is_base ? -1 : 1;
            if (a.position !== b.position)
                return a.position - b.position;
            return a.name.localeCompare(b.name);
        })
            .map((s) => s.name)
            .join("|");
    }, [scenarios.data]);
    // Signature of every scenario_chooser node's current input port names —
    // changes when a node is added/removed or its inputs drift. Drives a
    // re-sync of those nodes against the scenarios list.
    const chooserNodesSignature = useMemo(() => nodes
        .filter((n) => n.type === "scenario_chooser")
        .map((n) => {
        const ins = n.data.config?.inputs ?? [];
        return `${n.id}:${ins.map((i) => i?.name ?? "").join(",")}`;
    })
        .join("|"), [nodes]);
    useEffect(() => {
        if (!scenarios.data)
            return;
        const ordered = [...scenarios.data].sort((a, b) => {
            if (a.is_base !== b.is_base)
                return a.is_base ? -1 : 1;
            if (a.position !== b.position)
                return a.position - b.position;
            return a.name.localeCompare(b.name);
        });
        const desired = ordered.map((s) => ({
            id: s.name,
            name: s.name,
            description: s.description ?? "",
            include_lineage: true,
        }));
        const desiredJson = JSON.stringify(desired);
        setNodes((ns) => ns.map((n) => {
            if (n.type !== "scenario_chooser")
                return n;
            const currentInputs = n.data.config?.inputs ?? [];
            if (JSON.stringify(currentInputs) === desiredJson)
                return n;
            const nextConfig = { ...(n.data.config ?? {}), inputs: desired };
            const portData = derivePortData(n.type ?? "", nextConfig, n.data);
            return {
                ...n,
                data: { ...n.data, config: nextConfig, ...portData },
            };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenarioPortsSignature, chooserNodesSignature]);
    const onSelectionChange = useCallback((sel) => {
        setSelectedId(sel.nodes[0]?.id ?? null);
    }, []);
    const isValidConnection = useCallback((conn) => {
        const { source, target, sourceHandle, targetHandle } = conn;
        if (!source || !target || !sourceHandle || !targetHandle)
            return false;
        const sNode = nodes.find((n) => n.id === source);
        const tNode = nodes.find((n) => n.id === target);
        if (!sNode || !tNode)
            return false;
        const sKind = sNode.data.outputKinds?.[sourceHandle] ?? "value";
        const tKind = tNode.data.inputKinds?.[targetHandle] ?? "value";
        // Bus pairs only with bus, value pairs only with value.
        return sKind === tKind;
    }, [nodes]);
    const onConnect = useCallback((conn) => {
        const { source, target, sourceHandle, targetHandle } = conn;
        if (!source || !target || !sourceHandle || !targetHandle)
            return;
        setEdges((es) => {
            // One edge per (target, target-handle).
            const filtered = es.filter((x) => !(x.target === target && x.targetHandle === targetHandle));
            // Bus edges get a distinct stroke colour so it's visible at a glance.
            const sNode = nodes.find((n) => n.id === source);
            const isBus = (sNode?.data.outputKinds?.[sourceHandle] ?? "value") === "bus";
            const newEdge = {
                id: `${source}:${sourceHandle}->${target}:${targetHandle}`,
                source,
                sourceHandle,
                target,
                targetHandle,
                animated: false,
                style: {
                    stroke: isBus ? "#e879f9" : "#38bdf8",
                    strokeWidth: isBus ? 3 : 2,
                    strokeDasharray: isBus ? "6 3" : undefined,
                },
            };
            return [...filtered, newEdge];
        });
    }, [setEdges, nodes]);
    function nextId(prefix) {
        idCounter.current += 1;
        return `${prefix}_${Date.now().toString(36)}_${idCounter.current}`;
    }
    function addNodeAt(typeKey, position) {
        const nid = nextId(shortType(typeKey));
        let config;
        if (typeKey === "spreadsheet") {
            config = {
                cells: { A1: '=INPUT("x")', A2: "=A1" },
                named_ranges: [],
                outputs: [{ port: "value", cell: "A2" }],
            };
        }
        else if (typeKey === "output") {
            config = { currency: "USD", methodology: "" };
        }
        else if (typeKey === "assumption.input") {
            config = { source: "assumption", key: "" };
        }
        else if (typeKey === "ai.agent") {
            config = {
                agent_id: null,
                instructions: "",
                inputs: [],
                outputs: [{ id: portId(), name: "result" }],
            };
        }
        else if (typeKey === "inputs.all") {
            config = {};
        }
        else if (typeKey === "timeline_sheet") {
            config = {
                rows: [],
                periods: [],
                period_schedule: null,
                outputs: [],
            };
        }
        else {
            config = {};
        }
        const data = derivePortData(typeKey, config);
        setNodes((ns) => [
            ...ns,
            {
                id: nid,
                type: typeKey,
                position,
                data: {
                    label: defaultLabel(typeKey, config),
                    type: typeKey,
                    config,
                    ...data,
                },
            },
        ]);
        setSelectedId(nid);
    }
    const onPaneClick = useCallback((e) => {
        if (armedTool) {
            const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
            addNodeAt(armedTool, pos);
            setArmedTool(null);
            return;
        }
        setSelectedId(null);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [armedTool, rf]);
    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);
    const onDrop = useCallback((e) => {
        e.preventDefault();
        const typeKey = e.dataTransfer.getData("application/reactflow");
        if (!typeKey)
            return;
        const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        addNodeAt(typeKey, pos);
        setArmedTool(null);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rf]);
    function updateConfig(id, config) {
        setNodes((ns) => ns.map((n) => {
            if (n.id !== id)
                return n;
            const portData = derivePortData(n.type ?? "", config, n.data);
            return {
                ...n,
                data: {
                    ...n.data,
                    config,
                    label: defaultLabel(n.type ?? "", config),
                    ...portData,
                },
            };
        }));
    }
    function removeNode(id) {
        setNodes((ns) => ns.filter((n) => n.id !== id));
        setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
        if (selectedId === id)
            setSelectedId(null);
    }
    function buildDefn() {
        return {
            nodes: nodes.map((n) => ({
                id: n.id,
                type: n.type ?? "",
                config: n.data.config ?? {},
                position: { x: n.position.x, y: n.position.y },
            })),
            edges: edges.map((e) => ({
                id: e.id,
                from_node: e.source,
                from_port: e.sourceHandle ?? "",
                to_node: e.target,
                to_port: e.targetHandle ?? "",
            })),
        };
    }
    const currentJson = useMemo(() => JSON.stringify(buildDefn()), [nodes, edges]);
    const isDirty = lastSavedJson !== "" && currentJson !== lastSavedJson;
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    // Browser-level guard for tab close, refresh, or external navigation.
    // (In-page tab/crumb clicks are intercepted in the surrounding page.)
    useEffect(() => {
        if (!isDirty)
            return;
        function onBeforeUnload(e) {
            e.preventDefault();
            e.returnValue = "";
        }
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);
    async function handleSave() {
        const defn = buildDefn();
        const json = JSON.stringify(defn);
        await onSave(defn);
        setLastSavedJson(json);
        setLastSavedAt(new Date());
    }
    const selectedNode = useMemo(() => {
        const n = nodes.find((x) => x.id === selectedId);
        if (!n)
            return null;
        return {
            id: n.id,
            type: n.type ?? "",
            config: n.data.config ?? {},
            position: { x: n.position.x, y: n.position.y },
        };
    }, [nodes, selectedId]);
    const editingSpreadsheetNode = useMemo(() => {
        if (!openSpreadsheetId)
            return null;
        const n = nodes.find((x) => x.id === openSpreadsheetId);
        if (!n)
            return null;
        return {
            id: n.id,
            type: n.type ?? "",
            config: n.data.config ?? {},
        };
    }, [nodes, openSpreadsheetId]);
    const editingDerivativesNode = useMemo(() => {
        if (!openDerivativesId)
            return null;
        const n = nodes.find((x) => x.id === openDerivativesId);
        if (!n)
            return null;
        const cfg = n.data.config ?? {};
        return {
            id: n.id,
            config: {
                legs: cfg.legs ?? [],
                ir_model: cfg.ir_model,
                credit_model: cfg.credit_model,
                engine: cfg.engine,
                valuation_currency: cfg.valuation_currency,
                outputs: cfg.outputs ?? [],
                test_inputs: cfg.test_inputs ?? {},
            },
        };
    }, [nodes, openDerivativesId]);
    const editingTimelineNode = useMemo(() => {
        if (!openTimelineId)
            return null;
        const n = nodes.find((x) => x.id === openTimelineId);
        if (!n)
            return null;
        const cfg = n.data.config ?? {};
        return {
            id: n.id,
            config: {
                rows: cfg.rows ?? [],
                periods: cfg.periods ?? [],
                period_schedule: cfg.period_schedule ?? null,
                outputs: cfg.outputs ?? [],
            },
        };
    }, [nodes, openTimelineId]);
    return (_jsxs("div", { className: "flex h-[calc(100vh-12rem)] flex-col overflow-hidden rounded-lg border border-ink-700 bg-ink-900/40 sm:h-[calc(100vh-9rem)] sm:flex-row", children: [_jsx(Palette, { armedTool: armedTool, onArmTool: (t) => setArmedTool((cur) => (cur === t ? null : t)) }), _jsxs("div", { className: `relative min-h-0 flex-1 ${armedTool ? "cursor-crosshair" : ""}`, children: [_jsxs(ReactFlow, { nodes: nodes, edges: edges, onNodesChange: onNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, isValidConnection: isValidConnection, onSelectionChange: onSelectionChange, onPaneClick: onPaneClick, onDragOver: onDragOver, onDrop: onDrop, nodeTypes: NODE_TYPES, edgeTypes: EDGE_TYPES, fitView: nodes.length > 0, minZoom: 0.4, maxZoom: 1.6, defaultEdgeOptions: {
                            style: { stroke: "#38bdf8", strokeWidth: 2 },
                            interactionWidth: 24,
                        }, proOptions: { hideAttribution: true }, deleteKeyCode: ["Delete", "Backspace"], children: [_jsx(Background, { variant: BackgroundVariant.Dots, gap: 24, size: 1, color: "#22252d" }), _jsx(Controls, { className: "!bg-ink-800 !border-ink-700 !text-ink-300" })] }), nodes.length === 0 && _jsx(EmptyHint, { armed: armedTool }), prunedEdges.length > 0 && (_jsx("div", { className: "absolute left-4 right-4 top-4 z-10 rounded-md border border-amber-700/50 bg-amber-900/30 p-3 text-xs text-amber-200 shadow-md backdrop-blur", children: _jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsxs("div", { className: "font-medium text-amber-100", children: [prunedEdges.length, " orphan connection", prunedEdges.length === 1 ? " was" : "s were", " removed on load."] }), _jsxs("div", { className: "mt-1", children: ["These edges referenced ports that no longer exist on their nodes (likely renamed or replaced). Redraw any connections you still need, then click ", _jsx("strong", { children: "Save" }), " to persist the cleanup."] }), _jsxs("ul", { className: "mt-2 space-y-0.5 font-mono text-[11px] text-amber-300/90", children: [prunedEdges.slice(0, 5).map((e, i) => (_jsxs("li", { children: [e.from, ":", e.fromPort, " \u2192 ", e.to, ":", e.toPort] }, i))), prunedEdges.length > 5 && (_jsxs("li", { children: ["\u2026 and ", prunedEdges.length - 5, " more"] }))] })] }), _jsx("button", { onClick: () => setPrunedEdges([]), className: "text-amber-300 hover:text-amber-100", "aria-label": "dismiss", children: "\u00D7" })] }) })), armedTool && (_jsx("div", { className: "pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-full border border-accent-500/50 bg-accent-500/10 px-3 py-1 text-xs text-accent-200 shadow-md", children: "Click on the canvas to drop \u00B7 Esc to cancel" })), _jsxs("div", { className: "pointer-events-none absolute right-4 top-4 flex items-center gap-2", children: [_jsx("span", { className: `pointer-events-auto rounded-full px-2 py-0.5 text-[11px] ${isDirty
                                    ? "bg-amber-500/15 text-amber-200"
                                    : "bg-emerald-500/10 text-emerald-300"}`, title: lastSavedAt ? lastSavedAt.toLocaleString() : undefined, children: isDirty
                                    ? "Unsaved changes"
                                    : lastSavedAt
                                        ? `Saved ${formatRelative(lastSavedAt)}`
                                        : "Saved" }), _jsx("button", { className: "btn-ghost pointer-events-auto text-xs", onClick: () => setBuildWithAIOpen(true), title: "Run a Workflow Builder agent to design the calculation graph", children: "\u2728 Build with AI" }), _jsx("button", { className: "btn-ghost pointer-events-auto text-xs", onClick: () => setTemplateDialog("apply"), children: "Apply template" }), _jsx("button", { className: "btn-ghost pointer-events-auto text-xs", onClick: () => setTemplateDialog("save"), children: "Save as template" }), _jsx("button", { className: "btn-primary pointer-events-auto", disabled: saving || !isDirty, onClick: handleSave, children: saving ? "Saving…" : isDirty ? "Save" : "Saved" })] })] }), _jsx(ConfigPanel, { node: selectedNode, versionId: version.id, onChange: (next) => updateConfig(next.id, next.config), onRemove: removeNode, onOpenSpreadsheet: (id) => setOpenSpreadsheetId(id), onOpenTimeline: (id) => setOpenTimelineId(id), onOpenDerivatives: (id) => setOpenDerivativesId(id), onBuildModel: (id, toolType) => setBuildModelTarget({ nodeId: id, toolType }) }), editingSpreadsheetNode && (_jsx(SpreadsheetModal, { node: editingSpreadsheetNode, preview: spreadsheetPreviews[editingSpreadsheetNode.id] ?? null, valuationDate: version.valuation_date ?? null, loadPreviewInputs: async (nodeId) => {
                    return await api.post(`/workflow/approach-versions/${version.id}/preview-inputs`, {
                        node_id: nodeId,
                        definition: buildDefn(),
                    });
                }, onClose: () => setOpenSpreadsheetId(null), onSave: (cfg) => {
                    updateConfig(editingSpreadsheetNode.id, {
                        ...editingSpreadsheetNode.config,
                        cells: cfg.cells,
                        named_ranges: cfg.named_ranges,
                        outputs: cfg.outputs,
                        styles: cfg.styles,
                        test_inputs: cfg.test_inputs,
                    });
                    setOpenSpreadsheetId(null);
                } })), editingTimelineNode && (_jsx(TimelineEditor, { initial: editingTimelineNode.config, valuationDate: version.valuation_date ?? null, nodeId: editingTimelineNode.id, loadPreviewInputs: async (nid) => {
                    return await api.post(`/workflow/approach-versions/${version.id}/preview-inputs`, {
                        node_id: nid,
                        definition: buildDefn(),
                    });
                }, onClose: () => setOpenTimelineId(null), onSave: (cfg) => {
                    updateConfig(editingTimelineNode.id, cfg);
                    setOpenTimelineId(null);
                } })), editingDerivativesNode && (_jsx(DerivativesEditor, { initial: editingDerivativesNode.config, valuationDate: version.valuation_date ?? null, nodeId: editingDerivativesNode.id, loadPreviewInputs: async (nid) => {
                    return await api.post(`/workflow/approach-versions/${version.id}/preview-inputs`, {
                        node_id: nid,
                        definition: buildDefn(),
                    });
                }, onClose: () => setOpenDerivativesId(null), onSave: (cfg) => {
                    updateConfig(editingDerivativesNode.id, cfg);
                    setOpenDerivativesId(null);
                } })), buildModelTarget && (() => {
                const node = nodes.find((x) => x.id === buildModelTarget.nodeId);
                if (!node)
                    return null;
                const cfg = node.data.config ?? {};
                return (_jsx(BuildModelModal, { versionId: version.id, approachId: version.approach_id, instrumentId: null, toolType: buildModelTarget.toolType, currentConfig: cfg, onClose: () => setBuildModelTarget(null), onApplyConfig: (newConfig) => {
                        updateConfig(buildModelTarget.nodeId, newConfig);
                        setBuildModelTarget(null);
                    } }));
            })(), buildWithAIOpen && (_jsx(BuildWithAIModal, { versionId: version.id, currentDefn: buildDefn(), onClose: () => setBuildWithAIOpen(false), onApply: (defn) => {
                    // Re-init nodes/edges from the new defn — same path as the
                    // initial-load effect, but driven by user action.
                    const initialized = (defn.nodes || []).map((n) => {
                        const portData = derivePortData(n.type, n.config);
                        return {
                            id: n.id,
                            type: n.type,
                            position: n.position ?? { x: 120, y: 120 },
                            data: {
                                label: defaultLabel(n.type, n.config),
                                type: n.type,
                                config: n.config,
                                ...portData,
                            },
                        };
                    });
                    setNodes(initialized);
                    setEdges((defn.edges || []).map((e) => ({
                        id: e.id,
                        source: e.from_node,
                        sourceHandle: e.from_port,
                        target: e.to_node,
                        targetHandle: e.to_port,
                        animated: false,
                        style: { stroke: "#38bdf8", strokeWidth: 2 },
                    })));
                    setBuildWithAIOpen(false);
                } })), templateDialog === "save" && (_jsx(TemplateDialog, { mode: "save", versionId: version.id, buildDefinition: buildDefn, onClose: () => setTemplateDialog(null) })), templateDialog === "apply" && (_jsx(TemplateDialog, { mode: "apply", onClose: () => setTemplateDialog(null), onApply: (defn) => loadDefn(defn) }))] }));
    function loadDefn(defn) {
        const initialized = (defn.nodes ?? []).map((n) => {
            const portData = derivePortData(n.type, n.config ?? {});
            return {
                id: n.id,
                type: n.type,
                position: n.position ?? { x: 120, y: 120 },
                data: {
                    label: defaultLabel(n.type, n.config ?? {}),
                    type: n.type,
                    config: n.config ?? {},
                    ...portData,
                },
            };
        });
        setNodes(initialized);
        const inSet = new Map();
        const outSet = new Map();
        for (const n of initialized) {
            inSet.set(n.id, new Set(n.data.inputs));
            outSet.set(n.id, new Set(n.data.outputs));
        }
        const cleanEdges = (defn.edges ?? []).filter((e) => {
            const ins = inSet.get(e.to_node);
            const outs = outSet.get(e.from_node);
            return !!ins && ins.has(e.to_port) && !!outs && outs.has(e.from_port);
        });
        setEdges(cleanEdges.map((e) => ({
            id: e.id,
            source: e.from_node,
            sourceHandle: e.from_port,
            target: e.to_node,
            targetHandle: e.to_port,
            animated: false,
            style: { stroke: "#38bdf8", strokeWidth: 2 },
        })));
        setSelectedId(null);
    }
}
function EmptyHint({ armed }) {
    return (_jsx("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: _jsxs("div", { className: "rounded-xl border border-dashed border-ink-700 bg-ink-900/60 px-6 py-5 text-center text-sm text-ink-400", children: [_jsx("div", { className: "text-ink-200 font-medium", children: "Build the workflow" }), _jsx("div", { className: "mt-1", children: armed
                        ? "Click anywhere on the canvas to place your tool."
                        : "Click a tool on the left, then click on the canvas to place it." })] }) }));
}
function normalizeDefn(d) {
    if (!d || typeof d !== "object")
        return { nodes: [], edges: [] };
    const x = d;
    return { nodes: x.nodes ?? [], edges: x.edges ?? [] };
}
function defaultLabel(type, config) {
    // User-editable label always wins. Empty / whitespace falls back to
    // the type-derived default so nodes never render unlabelled.
    const userLabel = typeof config.label === "string" ? config.label.trim() : "";
    if (userLabel)
        return userLabel;
    if (type === "assumption.input")
        return config.key || "Input";
    if (type === "output")
        return "Instrument value";
    if (type === "spreadsheet")
        return "Spreadsheet";
    if (type === "ai.agent")
        return config.agent_name || "AI Agent";
    if (type === "inputs.all")
        return "All Inputs";
    if (type === "timeline_sheet")
        return "Timeline / Cash-flow";
    if (type === "scenario_chooser")
        return "Scenario chooser";
    if (type === "expression")
        return "Expression";
    if (type === "confidence_gate")
        return "Confidence gate";
    return type;
}
function shortType(t) {
    if (t === "assumption.input")
        return "asm";
    if (t === "spreadsheet")
        return "sheet";
    if (t === "output")
        return "out";
    if (t === "ai.agent")
        return "agent";
    if (t === "inputs.all")
        return "allin";
    if (t === "timeline_sheet")
        return "tline";
    return "node";
}
/** Static analysis of a timeline_sheet config:
 *  - inputs: every INPUT("name") referenced in any cell formula, plus
 *    `input:port` references in the period_schedule.
 *  - outputs: every explicitly declared output (in `config.outputs`)
 *    plus any ad-hoc OUTPUT("name", …) calls in cell formulas.
 */
function detectTimelinePorts(config) {
    const inputs = new Set();
    const outputs = new Set();
    // Cell formulas — scan INPUT() and OUTPUT().
    const rows = Array.isArray(config.rows)
        ? config.rows
        : [];
    for (const r of rows) {
        const cellMap = r.period_cells ?? {};
        for (const formula of Object.values(cellMap)) {
            if (typeof formula !== "string")
                continue;
            const re = /(INPUT|OUTPUT)\s*\(\s*"([^"]+)"/g;
            let m;
            while ((m = re.exec(formula)) !== null) {
                if (m[1] === "INPUT")
                    inputs.add(m[2]);
                else
                    outputs.add(m[2]);
            }
        }
    }
    // Schedule references like input:port_name → input.
    const sched = config.period_schedule;
    for (const ref of [sched?.anchor, sched?.until]) {
        if (typeof ref === "string" && ref.startsWith("input:")) {
            inputs.add(ref.slice(6));
        }
    }
    // Explicit output declarations.
    const outs = Array.isArray(config.outputs)
        ? config.outputs
        : [];
    for (const o of outs) {
        const port = o?.port ?? "";
        if (port)
            outputs.add(port);
    }
    return {
        inputs: Array.from(inputs).sort(),
        outputs: Array.from(outputs).sort(),
    };
}
/** Static analysis of a derivatives config:
 *  - inputs: every `input:port` reference in legs (schedule anchors/
 *    until, discount_curve, amortisation_schedule, numeric fields like
 *    notional/coupon/recovery_rate when supplied as input refs), plus
 *    any INPUT("name") inside a payoff or exercise script, plus the
 *    IR/credit `params_from` keys.
 *  - outputs: every declared output port.
 */
function detectDerivativesPorts(config) {
    const inputs = new Set();
    const outputs = new Set();
    const visit = (v) => {
        if (typeof v !== "string")
            return;
        const m = /^input:(.+)$/.exec(v.trim());
        if (m)
            inputs.add(m[1]);
    };
    const scanScript = (script) => {
        if (typeof script !== "string")
            return;
        const re = /INPUT\s*\(\s*"([^"]+)"/g;
        let m;
        while ((m = re.exec(script)) !== null)
            inputs.add(m[1]);
    };
    const legs = Array.isArray(config.legs)
        ? config.legs : [];
    for (const l of legs) {
        const sched = l.schedule ?? {};
        visit(sched.anchor);
        visit(sched.until);
        visit(l.discount_curve);
        visit(l.amortisation_schedule);
        visit(l.notional);
        visit(l.coupon);
        visit(l.recovery_rate);
        scanScript(l.payoff);
        const ex = l.exercise;
        if (ex)
            scanScript(ex.payoff);
    }
    const ir = config.ir_model;
    if (ir?.params?.params_from && typeof ir.params.params_from === "string") {
        inputs.add(ir.params.params_from);
    }
    const credit = config.credit_model;
    if (credit?.params?.params_from && typeof credit.params.params_from === "string") {
        inputs.add(credit.params.params_from);
    }
    const outs = Array.isArray(config.outputs)
        ? config.outputs : [];
    for (const o of outs) {
        const port = o?.port ?? "";
        if (port)
            outputs.add(port);
    }
    return {
        inputs: Array.from(inputs).sort(),
        outputs: Array.from(outputs).sort(),
    };
}
function staticInputs(t) {
    if (t === "output")
        return ["value"];
    if (t === "confidence_gate")
        return ["value"];
    return [];
}
function staticOutputs(t) {
    if (t === "assumption.input")
        return ["value"];
    if (t === "inputs.all")
        return ["all"];
    if (t === "scenario_chooser")
        return ["value"];
    if (t === "expression")
        return ["value"];
    if (t === "confidence_gate")
        return ["value"];
    return [];
}
// Per-node-type port-kind tables. Bus ports are listed here; everything
// else defaults to "value" in derivePortData below.
const BUS_INPUTS = {
    spreadsheet: ["bus"],
    timeline_sheet: ["bus"],
    derivatives: ["bus"],
};
const BUS_OUTPUTS = {
    "inputs.all": ["all"],
};
/**
 * Accept either legacy [string] or current [{id, name, …}] shapes and
 * return the unified AgentPort form. For legacy string entries the name
 * doubles as the id so existing edges keep working without redrawing.
 */
export function normalizeAgentPorts(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (typeof item === "string") {
            if (item)
                out.push({ id: item, name: item, include_lineage: true });
        }
        else if (item && typeof item === "object" && "id" in item) {
            const o = item;
            out.push({
                id: String(o.id),
                name: String(o.name ?? ""),
                description: o.description ? String(o.description) : "",
                data_type: o.data_type ? String(o.data_type) : undefined,
                include_lineage: o.include_lineage !== false,
            });
        }
    }
    return out;
}
function portId() {
    // crypto.randomUUID is widely supported on modern browsers and gives us
    // a stable handle id that is independent of the user-typed port name.
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return "p_" + crypto.randomUUID().replace(/-/g, "").slice(0, 10);
    }
    return "p_" + Math.random().toString(36).slice(2, 12);
}
function derivePortData(typeKey, config, prevData) {
    const busIns = BUS_INPUTS[typeKey] ?? [];
    const busOuts = BUS_OUTPUTS[typeKey] ?? [];
    const kindMap = (bus, values) => ({
        ...Object.fromEntries(values.map((v) => [v, "value"])),
        ...Object.fromEntries(bus.map((v) => [v, "bus"])),
    });
    if (typeKey === "spreadsheet") {
        const detected = detectSpreadsheetPorts(config.cells, config.outputs);
        // Bus inputs come first so they appear at the top of the port column.
        const inputs = [...busIns, ...detected.inputs];
        return {
            inputs,
            outputs: detected.outputs,
            inputKinds: kindMap(busIns, detected.inputs),
            outputKinds: kindMap([], detected.outputs),
        };
    }
    if (typeKey === "timeline_sheet") {
        const detected = detectTimelinePorts(config);
        const inputs = [...busIns, ...detected.inputs];
        return {
            inputs,
            outputs: detected.outputs,
            inputKinds: kindMap(busIns, detected.inputs),
            outputKinds: kindMap([], detected.outputs),
        };
    }
    if (typeKey === "derivatives") {
        const detected = detectDerivativesPorts(config);
        const inputs = [...busIns, ...detected.inputs];
        return {
            inputs,
            outputs: detected.outputs,
            inputKinds: kindMap(busIns, detected.inputs),
            outputKinds: kindMap([], detected.outputs),
        };
    }
    if (typeKey === "ai.agent") {
        const ins = normalizeAgentPorts(config.inputs);
        const outs = normalizeAgentPorts(config.outputs);
        return {
            inputs: ins.map((p) => p.id),
            outputs: outs.map((p) => p.id),
            inputLabels: Object.fromEntries(ins.map((p) => [p.id, p.name])),
            outputLabels: Object.fromEntries(outs.map((p) => [p.id, p.name])),
            inputKinds: Object.fromEntries(ins.map((p) => [p.id, "value"])),
            outputKinds: Object.fromEntries(outs.map((p) => [p.id, "value"])),
        };
    }
    if (typeKey === "scenario_chooser" || typeKey === "expression") {
        // Both nodes derive their input handles from `config.inputs`
        // (same {id, name} shape as ai.agent) and emit a single static
        // `value` output port. scenario_chooser's port names are the
        // scenario names ("Base", "Bull"); expression's port names are
        // identifiers referenced by INPUT("name") in the formula.
        const ins = normalizeAgentPorts(config.inputs);
        return {
            inputs: ins.map((p) => p.id),
            outputs: ["value"],
            inputLabels: Object.fromEntries(ins.map((p) => [p.id, p.name])),
            outputLabels: { value: "value" },
            inputKinds: Object.fromEntries(ins.map((p) => [p.id, "value"])),
            outputKinds: { value: "value" },
        };
    }
    const inputs = prevData?.inputs ?? staticInputs(typeKey);
    const outputs = prevData?.outputs ?? staticOutputs(typeKey);
    return {
        inputs,
        outputs,
        inputKinds: kindMap(busIns, inputs.filter((n) => !busIns.includes(n))),
        outputKinds: kindMap(busOuts, outputs.filter((n) => !busOuts.includes(n))),
    };
}
function arraysEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i])
            return false;
    return true;
}
function formatRelative(d) {
    const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 5)
        return "just now";
    if (secs < 60)
        return `${secs}s ago`;
    if (secs < 3600)
        return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400)
        return `${Math.floor(secs / 3600)}h ago`;
    return d.toLocaleString();
}
